import type React from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
} from "@mui/material"
import {
  CreditCard as CreditCardIcon,
  ReceiptLong as ReceiptIcon,
  FileDownload as DownloadIcon,
  Add as AddIcon,
} from "@mui/icons-material"

const PaymentsSection: React.FC = () => {
  // Mock payment history data
  const payments = [
    {
      id: "INV-2025-001",
      date: "June 09, 2025",
      description: "Premium Membership - Monthly",
      amount: 19.99,
      status: "completed",
      invoiceUrl: "#",
    },
    {
      id: "INV-2025-002",
      date: "June 05, 2025",
      description: "Softball Cage1 Booking",
      amount: 1.13,
      status: "refunded",
      invoiceUrl: "#",
    },
    {
      id: "INV-2025-003",
      date: "May 28, 2025",
      description: "Tennis Court 3 Booking",
      amount: 25.0,
      status: "completed",
      invoiceUrl: "#",
    },
    {
      id: "INV-2025-004",
      date: "May 15, 2025",
      description: "Basketball Court Booking",
      amount: 15.5,
      status: "completed",
      invoiceUrl: "#",
    },
  ]

  // Mock payment methods
  const paymentMethods = [
    {
      id: "pm_1",
      type: "visa",
      last4: "4242",
      expiry: "06/2026",
      isDefault: true,
    },
    {
      id: "pm_2",
      type: "mastercard",
      last4: "5555",
      expiry: "08/2025",
      isDefault: false,
    },
  ]

  // Get status chip
  const getStatusChip = (status: string) => {
    switch (status) {
      case "completed":
        return <Chip size="small" label="Completed" color="success" />
      case "pending":
        return <Chip size="small" label="Pending" color="warning" />
      case "failed":
        return <Chip size="small" label="Failed" color="error" />
      case "refunded":
        return <Chip size="small" label="Refunded" color="info" />
      default:
        return <Chip size="small" label={status} />
    }
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Payments
      </Typography>

      {/* Payment Methods */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Payment Methods</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} size="small">
            Add New
          </Button>
        </Box>

        <Grid container spacing={2}>
          {paymentMethods.map((method) => (
            <Grid item xs={12} sm={6} md={4} key={method.id}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CreditCardIcon />
                      <Typography variant="body1" fontWeight={500}>
                        •••• {method.last4}
                      </Typography>
                    </Box>
                    {method.isDefault && <Chip label="Default" size="small" color="primary" />}
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Expires: {method.expiry}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                    <Button size="small" variant="outlined">
                      Edit
                    </Button>
                    {!method.isDefault && (
                      <Button size="small" variant="outlined" color="error">
                        Remove
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Payment History */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Payment History</Typography>
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <ReceiptIcon fontSize="small" color="action" />
                      <Typography variant="body2">{payment.id}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{payment.date}</TableCell>
                  <TableCell>{payment.description}</TableCell>
                  <TableCell>{getStatusChip(payment.status)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body1" fontWeight={500}>
                      ${payment.amount.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Download Invoice">
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}

export default PaymentsSection
